import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'

import {
  Box, DashboardModule, ListItem, Text,
} from '@campaignhub/suit-theme'

import useReduxAction from '@hooks/useReduxAction'
import useServiceJobChecklistItem from '@hooks/useServiceJobChecklistItem'
import ChecklistItems from '../ChecklistItems'
import MutationStatus from '../MutationStatus'

// IDs match `ServiceJobCheckListItemType` in the backend.
const SECTION_TYPE_IDS = [
  4, // ClientOrder            - "Client Order List"
  6, // ProviderNote           - "Provider Notes"
  5, // PostProductionNote     - "Post-Production Notes"
  7, // PostProductionChecklist- "Post-Production Checklist"
  8, // OtherNote              - "Other Notes"
]

const EDITABLE_TYPE_IDS = [8]

const PostProductionChecklist = (props) => {
  const { serviceJobId, hidden } = props

  // Pull all checklist items for this job (BE includes shared null-role items by default).
  useReduxAction('serviceJobChecklistItems', 'loadServiceJobChecklistItems', { serviceJobs: serviceJobId }, [serviceJobId], {
    shouldPerformFn: (entityReducer) => {
      const { loading, errors } = entityReducer
      return !!serviceJobId && !loading && !errors?.length
    },
  })

  const { creating, updating, deleting } = useServiceJobChecklistItem()

  // Types are loaded once at the section level (Studio/OnSite/BackOffice) via
  // useServiceJobChecklistItemTypes, matching how the asset page consumes them.
  const entities = useSelector(reduxState => reduxState.entities)
  const { serviceJobChecklistItems, serviceJobChecklistItemTypes } = entities

  const itemsForJob = useMemo(
    () => Object.values(serviceJobChecklistItems || {})
      .filter(item => item.serviceJobId === Number(serviceJobId)),
    [serviceJobChecklistItems, serviceJobId],
  )

  if (hidden) return null

  const itemsByType = SECTION_TYPE_IDS.reduce((acc, typeId) => {
    acc[typeId] = itemsForJob.filter(x => x.serviceJobCheckListItemTypeId === typeId)
    return acc
  }, {})

  const headerLabel = typeId => serviceJobChecklistItemTypes?.[typeId]?.description

  return (
    <DashboardModule
      title="Post-Production Checklist"
      headerRight={<MutationStatus isMutating={creating || updating || deleting} />}
    >
      <Box flexDirection="column" lineHeight="1.3">
        {SECTION_TYPE_IDS.map((typeId) => {
          const items = itemsByType[typeId]
          if (!items.length && !EDITABLE_TYPE_IDS.includes(typeId)) return null
          return (
            <ListItem
              key={typeId}
              boxProps={{
                borderBottom: '1px solid',
                padding: 'large',
              }}
              flexDirection="column"
              disableHover
            >
              <Text fontSize="small" paddingBottom="medium">{headerLabel(typeId)}</Text>
              <ChecklistItems
                addPadding="0px 8px"
                checkable
                editable={EDITABLE_TYPE_IDS.includes(typeId)}
                hideItemBorderBottom
                itemPadding="6px 12px"
                title={headerLabel(typeId)}
                serviceJobId={serviceJobId}
                serviceJobChecklistItemTypeId={typeId}
              />
            </ListItem>
          )
        })}
      </Box>
    </DashboardModule>
  )
}

PostProductionChecklist.propTypes = {
  serviceJobId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  hidden: PropTypes.bool,
}

export default PostProductionChecklist
